import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { SectionTiles } from '../components/SectionTiles';
import { EnterForm } from '../components/EnterForm';
import { FormShortcutHints } from '../components/ui/form-shortcut-hints';
import { SearchableSelect } from '../components/ui/searchable-select';
import { DateInput } from '../components/ui/date-input';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { formatDate, getToday, formatApiErrorDetail } from '../lib/utils';
import { Package, Users, Building2, UserCog, BookOpen, Receipt, Plus, Pencil, Trash2, AlertCircle, X, RefreshCw, ChevronDown, ChevronRight } from 'lucide-react';
import { Alert, AlertDescription } from '../components/ui/alert';
import { useSortableTable, SortableHeader } from '../components/ui/sortable-table';
import { usePageCallbacks } from '../hooks/usePageCallbacks';
import { PhoneInput } from '../components/ui/phone-input';
import {
  getProducts, createProduct, updateProduct, deleteProduct, bulkDeleteProducts,
  getHSNCodes, createHSN, updateHSN, deleteHSN, deleteHSNAll,
  getCustomers, createCustomer, updateCustomer, deleteCustomer, bulkDeleteCustomers,
  getSupplierGroups, createSupplierGroup, updateSupplierGroup, deleteSupplierGroup,
  getSuppliers, createSupplier, updateSupplier, deleteSupplier,
  getEntryLedgers, createEntryLedger, deleteEntryLedger,
  getProductsMeta
} from '../lib/api';

const emptyContact = { name: '', department: '', mobile_number: '', email: '' };

const CATEGORY_LABELS = {
  raw_material: 'Raw Material',
  in_process: 'In-Process',
  finished_goods: 'Finished Goods',
};

const CATEGORY_OPTIONS = [
  { value: 'raw_material', label: 'Raw Material' },
  { value: 'in_process', label: 'In-Process' },
  { value: 'finished_goods', label: 'Finished Goods' },
];
export default function Master() {
  const location = useLocation();
  const defaultTab = location.state?.tab || '';
  const [activeTab, setActiveTab] = useState(defaultTab);

  const SECTION_DEFS = [
    { key: '1', value: 'products', label: 'Products', icon: Package },
    { key: '2', value: 'customers', label: 'Customers', icon: Users },
    { key: '3', value: 'supplier-groups', label: 'Supplier Groups', icon: Building2 },
    { key: '4', value: 'supplier-firms', label: 'Supplier Firms', icon: UserCog },
    { key: '5', value: 'entry-ledger', label: 'Entry Ledger', icon: BookOpen },
    { key: '6', value: 'hsn', label: 'HSN', icon: Receipt },
  ];

  // ===== PRODUCTS =====
  const [products, setProducts] = useState([]);
  const [productsMeta, setProductsMeta] = useState({ categories: [], groups: [] });
  const [hsnCodes, setHsnCodes] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [productDialog, setProductDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productForm, setProductForm] = useState({ name: '', category: '', group: '', kg_per_bag: '', brands: '', hsn_code: '', default_gst_rate: '', description: '' });
  const [productError, setProductError] = useState('');
  const [productSubmitting, setProductSubmitting] = useState(false);
  const [filterCategory, setFilterCategory] = useState('');
  const [filterGroup, setFilterGroup] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [expandedMobileId, setExpandedMobileId] = useState(null);
  // Inline HSN creation
  const [addHsnOpen, setAddHsnOpen] = useState(false);
  const [addHsnForm, setAddHsnForm] = useState({ hsn_code: '', description: '', gst_rate: '', applicable_from: getToday() });
  const [addHsnSubmitting, setAddHsnSubmitting] = useState(false);

  // Sorting
  const { sortedData: sortedProducts, sortKey: prodSortKey, sortDirection: prodSortDir, handleSort: handleProdSort } = useSortableTable(products, 'name', 'asc');

  // Keyboard shortcuts for category/group filters on Products tab
  const gPrefixRef = useRef(false);
  useEffect(() => {
    const CATEGORY_KEYS = ['', 'raw_material', 'in_process', 'finished_goods'];
    let gTimeout = null;
    const handleFilterKeys = (e) => {
      if (activeTab !== 'products') return;
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.closest('[role="dialog"]')) return;
      if (e.key === 'g' || e.key === 'G') {
        e.preventDefault();
        gPrefixRef.current = true;
        clearTimeout(gTimeout);
        gTimeout = setTimeout(() => { gPrefixRef.current = false; }, 1000);
        return;
      }
      const num = parseInt(e.key);
      if (gPrefixRef.current && num >= 0 && num <= 9) {
        e.preventDefault();
        gPrefixRef.current = false;
        const allGroups = ['', ...productsMeta.groups];
        if (num < allGroups.length) handleGroupFilter(allGroups[num]);
        return;
      }
      if (num >= 1 && num <= 4) {
        e.preventDefault();
        handleCategoryFilter(CATEGORY_KEYS[num - 1] || '');
      }
    };
    document.addEventListener('keydown', handleFilterKeys);
    return () => { document.removeEventListener('keydown', handleFilterKeys); clearTimeout(gTimeout); };
  }, [activeTab, filterGroup, productsMeta.groups]); // eslint-disable-line

  usePageCallbacks({
    onNewEntry: () => {
      if (activeTab === 'products') { resetProductForm(); setProductDialog(true); }
    },
  });

  const fetchProducts = async (cat, grp) => {
    setProductsLoading(true);
    try {
      const [pRes, mRes, hRes] = await Promise.all([getProducts(cat || filterCategory, grp || filterGroup), getProductsMeta(), getHSNCodes()]);
      setProducts(pRes.data); setProductsMeta(mRes.data); setHsnCodes(hRes.data);
    } catch (err) { console.error('Failed to load products:', err); toast.error('Failed to load products'); }
    finally { setProductsLoading(false); }
  };
  const fetchFilteredProducts = async (cat, grp) => {
    try { const res = await getProducts(cat, grp); setProducts(res.data); }
    catch { toast.error('Failed to filter'); }
  };
  const handleCategoryFilter = (cat) => { setFilterCategory(cat); setSelectedIds([]); fetchFilteredProducts(cat, filterGroup); };
  const handleGroupFilter = (grp) => { setFilterGroup(grp); setSelectedIds([]); fetchFilteredProducts(filterCategory, grp); };
  const resetProductForm = () => { setProductForm({ name: '', category: '', group: '', kg_per_bag: '', brands: '', hsn_code: '', default_gst_rate: '', description: '' }); setEditingProduct(null); setProductError(''); };
  const openProductDialog = (p) => {
    if (p) {
      setEditingProduct(p);
      setProductForm({
        name: p.name, category: p.category, group: p.group,
        kg_per_bag: p.kg_per_bag?.toString() || '', brands: (p.brands || []).join(', '),
        hsn_code: p.hsn_code || '', default_gst_rate: p.default_gst_rate ? String(p.default_gst_rate) : '',
        description: p.description || ''
      });
    } else {
      resetProductForm();
    }
    setProductError(''); setProductDialog(true);
  };
  const handleProductSubmit = async (e) => {
    e.preventDefault(); setProductError(''); setProductSubmitting(true);
    try {
      const payload = {
        name: productForm.name, category: productForm.category, group: productForm.group,
        kg_per_bag: parseFloat(productForm.kg_per_bag) || 50.0,
        brands: productForm.brands ? productForm.brands.split(',').map(b => b.trim()).filter(Boolean) : [],
        hsn_code: productForm.hsn_code || '',
        default_gst_rate: productForm.default_gst_rate ? parseFloat(productForm.default_gst_rate) : 0,
        description: productForm.description || ''
      };
      if (editingProduct) { await updateProduct(editingProduct.id, payload); toast.success('Product updated'); }
      else { await createProduct(payload); toast.success('Product created'); }
      setProductDialog(false); resetProductForm(); fetchProducts();
    } catch (err) { setProductError(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setProductSubmitting(false); }
  };
  const handleDeleteProduct = async (p) => {
    if (!window.confirm(`Delete "${p.name}"?`)) return;
    try { await deleteProduct(p.id); toast.success('Deleted'); fetchProducts(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const handleBulkDeleteProducts = async () => {
    if (!window.confirm(`Delete ${selectedIds.length} products?`)) return;
    try { await bulkDeleteProducts(selectedIds); toast.success('Deleted'); setSelectedIds([]); fetchProducts(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const allSelected = products.length > 0 && products.every(p => selectedIds.includes(p.id));
  const toggleSelect = (id) => setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const toggleAll = () => { if (allSelected) setSelectedIds([]); else setSelectedIds(products.map(p => p.id)); };
  const handleAddHsnSubmit = async (e) => {
    e.preventDefault();
    if (!addHsnForm.hsn_code || !addHsnForm.gst_rate) { toast.error('HSN Code and GST Rate are required'); return; }
    setAddHsnSubmitting(true);
    try {
      await createHSN({ hsn_code: addHsnForm.hsn_code, description: addHsnForm.description, gst_rate: parseFloat(addHsnForm.gst_rate), applicable_from: addHsnForm.applicable_from });
      toast.success('HSN created'); setAddHsnOpen(false);
      const hRes = await getHSNCodes(); setHsnCodes(hRes.data);
      const newHsn = hRes.data.find(h => h.hsn_code === addHsnForm.hsn_code);
      setProductForm(p => ({ ...p, hsn_code: addHsnForm.hsn_code, default_gst_rate: newHsn ? String(newHsn.gst_rate) : p.default_gst_rate }));
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setAddHsnSubmitting(false); }
  };
  const hsnOptions = (() => { const seen = new Set(); return hsnCodes.filter(h => { if (seen.has(h.hsn_code)) return false; seen.add(h.hsn_code); return true; }).map(h => ({ value: h.hsn_code, label: `${h.hsn_code} - ${h.description || 'N/A'}` })); })();
  const groupOptions = (productsMeta.groups || []).map(g => ({ value: g, label: g }));

  // ===== CUSTOMERS =====
  const [customers, setCustomers] = useState([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [customerDialog, setCustomerDialog] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [customerForm, setCustomerForm] = useState({ company_name: '', company_email: '', contact_persons: [{ ...emptyContact }], credit_days: '30' });
  const [customerError, setCustomerError] = useState('');
  const [customerSubmitting, setCustomerSubmitting] = useState(false);
  const [selectedCustomerIds, setSelectedCustomerIds] = useState([]);

  const { sortedData: sortedCustomers, sortKey: custSortKey, sortDirection: custSortDir, handleSort: handleCustSort } = useSortableTable(customers, 'company_name', 'asc');

  const fetchCustomers = async () => { setCustomersLoading(true); try { const r = await getCustomers(); setCustomers(r.data); } catch (err) { console.error('Failed to load customers:', err); } finally { setCustomersLoading(false); } };
  const resetCustomerForm = () => {
    setCustomerForm({ company_name: '', company_email: '', contact_persons: [{ ...emptyContact }], credit_days: '30' });
    setEditingCustomer(null); setCustomerError('');
  };
  const openCustomerDialog = (c) => {
    if (c) {
      setEditingCustomer(c);
      setCustomerForm({
        company_name: c.company_name || c.name || '', company_email: c.company_email || '',
        contact_persons: c.contact_persons?.length > 0 ? c.contact_persons : [{ ...emptyContact }],
        credit_days: (c.credit_days || 30).toString()
      });
    } else { resetCustomerForm(); }
    setCustomerError(''); setCustomerDialog(true);
  };
  const handleCustomerSubmit = async (e) => {
    e.preventDefault(); setCustomerError(''); setCustomerSubmitting(true);
    const payload = {
      company_name: customerForm.company_name, company_email: customerForm.company_email,
      contact_persons: customerForm.contact_persons.filter(cp => cp.name || cp.department || cp.mobile_number || cp.email),
      credit_days: parseInt(customerForm.credit_days)
    };
    try {
      if (editingCustomer) { await updateCustomer(editingCustomer.id, payload); toast.success('Updated'); }
      else { await createCustomer(payload); toast.success('Created'); }
      setCustomerDialog(false); resetCustomerForm(); fetchCustomers();
    } catch (err) { setCustomerError(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setCustomerSubmitting(false); }
  };
  const handleDeleteCustomer = async (c) => { if (!window.confirm(`Delete "${c.company_name || c.name}"?`)) return; try { await deleteCustomer(c.id); toast.success('Deleted'); fetchCustomers(); } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } };
  const addContactPerson = () => setCustomerForm(prev => ({ ...prev, contact_persons: [...prev.contact_persons, { ...emptyContact }] }));
  const removeContactPerson = (idx) => setCustomerForm(prev => ({ ...prev, contact_persons: prev.contact_persons.filter((_, i) => i !== idx) }));
  const updateContactPerson = (idx, field, value) => setCustomerForm(prev => ({ ...prev, contact_persons: prev.contact_persons.map((cp, i) => i === idx ? { ...cp, [field]: value } : cp) }));
  const allCustomersSelected = customers.length > 0 && customers.every(c => selectedCustomerIds.includes(c.id));
  const toggleCustomerSelect = (id) => setSelectedCustomerIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const toggleAllCustomers = () => { if (allCustomersSelected) setSelectedCustomerIds([]); else setSelectedCustomerIds(customers.map(c => c.id)); };
  const handleBulkDeleteCustomers = async () => {
    if (!window.confirm(`Delete ${selectedCustomerIds.length} selected customers?`)) return;
    try { await bulkDeleteCustomers(selectedCustomerIds); toast.success('Deleted'); setSelectedCustomerIds([]); fetchCustomers(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  // ===== SUPPLIER GROUPS =====
  const [supplierGroups, setSupplierGroups] = useState([]);
  const [sgLoading, setSgLoading] = useState(false);
  const [sgDialog, setSgDialog] = useState(false);
  const [editingSG, setEditingSG] = useState(null);
  const [sgForm, setSgForm] = useState({ name: '', credit_days: '30' });
  const [sgError, setSgError] = useState('');
  const [sgSubmitting, setSgSubmitting] = useState(false);

  const fetchSG = async () => { setSgLoading(true); try { const r = await getSupplierGroups(); setSupplierGroups(r.data); } catch (err) { console.error('Failed to load supplier groups:', err); } finally { setSgLoading(false); } };
  const openSGDialog = (g) => {
    if (g) { setEditingSG(g); setSgForm({ name: g.name, credit_days: String(g.credit_days || 30) }); }
    else { setEditingSG(null); setSgForm({ name: '', credit_days: '30' }); }
    setSgError(''); setSgDialog(true);
  };
  const handleSGSubmit = async (e) => {
    e.preventDefault(); setSgError(''); setSgSubmitting(true);
    try {
      const payload = { name: sgForm.name, credit_days: parseInt(sgForm.credit_days) || 30 };
      if (editingSG) await updateSupplierGroup(editingSG.id, payload);
      else await createSupplierGroup(payload);
      toast.success(editingSG ? 'Updated' : 'Created'); setSgDialog(false); fetchSG();
    } catch (err) { setSgError(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSgSubmitting(false); }
  };
  const handleDeleteSG = async (g) => { if (!window.confirm(`Delete ${g.name}?`)) return; try { await deleteSupplierGroup(g.id); toast.success('Deleted'); fetchSG(); } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } };

  // ===== SUPPLIER FIRMS =====
  const [suppliers, setSuppliers] = useState([]);
  const [suppliersLoading, setSuppliersLoading] = useState(false);
  const [supplierDialog, setSupplierDialog] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [supplierForm, setSupplierForm] = useState({ company_name: '', group_id: '', credit_days: '30', gstin: '', contact_person: '', phone: '', address: '' });
  const [supplierError, setSupplierError] = useState('');
  const [supplierSubmitting, setSupplierSubmitting] = useState(false);

  const fetchSuppliers = async () => { setSuppliersLoading(true); try { const [s, g] = await Promise.all([getSuppliers(), getSupplierGroups()]); setSuppliers(s.data); setSupplierGroups(g.data); } catch (err) { console.error('Failed to load suppliers:', err); } finally { setSuppliersLoading(false); } };
  const openSupplierDialog = (s) => {
    if (s) { setEditingSupplier(s); setSupplierForm({ company_name: s.company_name || '', group_id: s.group_id || '', credit_days: String(s.credit_days || 30), gstin: s.gstin || '', contact_person: s.contact_person || '', phone: s.phone || '', address: s.address || '' }); }
    else { setEditingSupplier(null); setSupplierForm({ company_name: '', group_id: '', credit_days: '30', gstin: '', contact_person: '', phone: '', address: '' }); }
    setSupplierError(''); setSupplierDialog(true);
  };
  const handleSupplierSubmit = async (e) => {
    e.preventDefault(); setSupplierError(''); setSupplierSubmitting(true);
    if (!supplierForm.group_id) { setSupplierError('Supplier Group is required'); setSupplierSubmitting(false); return; }
    const group = supplierGroups.find(g => g.id === supplierForm.group_id);
    try {
      const payload = { ...supplierForm, group_name: group?.name || '', credit_days: parseInt(supplierForm.credit_days) || 30 };
      if (editingSupplier) await updateSupplier(editingSupplier.id, payload);
      else await createSupplier(payload);
      toast.success(editingSupplier ? 'Updated' : 'Created'); setSupplierDialog(false); fetchSuppliers();
    } catch (err) { setSupplierError(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSupplierSubmitting(false); }
  };
  const handleDeleteSupplier = async (s) => { if (!window.confirm(`Delete ${s.company_name}?`)) return; try { await deleteSupplier(s.id); toast.success('Deleted'); fetchSuppliers(); } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } };

  // ===== ENTRY LEDGER =====
  const [entryLedgers, setEntryLedgers] = useState([]);
  const [elLoading, setElLoading] = useState(false);
  const [elDialog, setElDialog] = useState(false);
  const [elForm, setElForm] = useState({ name: '', category: '', type: 'income' });
  const [elSubmitting, setElSubmitting] = useState(false);

  const fetchEL = async () => { setElLoading(true); try { const r = await getEntryLedgers(); setEntryLedgers(r.data); } catch (err) { console.error('Failed to load entry ledgers:', err); } finally { setElLoading(false); } };
  const handleELSubmit = async (e) => {
    e.preventDefault(); setElSubmitting(true);
    try { await createEntryLedger(elForm); toast.success('Created'); setElDialog(false); fetchEL(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setElSubmitting(false); }
  };

  // ===== HSN =====
  const [hsnList, setHsnList] = useState([]);
  const [hsnLoading, setHsnLoading] = useState(false);
  const [expandedHsn, setExpandedHsn] = useState(null);
  const [selectedHsnCodes, setSelectedHsnCodes] = useState([]);
  // Add/Edit HSN dialog
  const [hsnDialog, setHsnDialog] = useState(false);
  const [editingHsn, setEditingHsn] = useState(null);
  const [hsnForm, setHsnForm] = useState({ hsn_code: '', description: '', gst_rate: '', applicable_from: '' });
  const [hsnSubmitting, setHsnSubmitting] = useState(false);
  const [hsnError, setHsnError] = useState('');
  // Update GST Rate dialog
  const [rateDialog, setRateDialog] = useState(false);
  const [rateUpdates, setRateUpdates] = useState({});
  const [rateSubmitting, setRateSubmitting] = useState(null);
  // Per-row quick rate update
  const [quickRateDialog, setQuickRateDialog] = useState(false);
  const [quickRateHsn, setQuickRateHsn] = useState(null);
  const [quickRateForm, setQuickRateForm] = useState({ gst_rate: '', applicable_from: getToday() });
  const [quickRateSubmitting, setQuickRateSubmitting] = useState(false);

  const fetchHSN = async () => { setHsnLoading(true); try { const r = await getHSNCodes(); setHsnList(r.data); } catch (err) { console.error('Failed to load HSN codes:', err); } finally { setHsnLoading(false); } };

  // 'n' shortcut to create new HSN
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'n' && !e.ctrlKey && !e.metaKey && activeTab === 'hsn' && !hsnDialog && !rateDialog && !quickRateDialog && !document.querySelector('[role="dialog"]')) {
        e.preventDefault(); openHsnDialog();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [activeTab, hsnDialog, rateDialog, quickRateDialog]);

  // Group HSN codes by hsn_code, pick current (no effective_to) as primary
  const groupedHsn = (() => {
    const map = {};
    hsnList.forEach(h => { if (!map[h.hsn_code]) map[h.hsn_code] = []; map[h.hsn_code].push(h); });
    return Object.entries(map).map(([code, entries]) => {
      const sorted = entries.sort((a, b) => (b.applicable_from || '').localeCompare(a.applicable_from || ''));
      // "Current" is the one with no effective_to (open-ended), fallback to latest by applicable_from
      const current = sorted.find(e => !e.effective_to) || sorted[0];
      return { hsn_code: code, latest: current, history: sorted, description: current.description || '' };
    }).sort((a, b) => a.hsn_code.localeCompare(b.hsn_code));
  })();

  const openHsnDialog = (hsn = null) => {
    if (hsn) {
      setEditingHsn(hsn);
      setHsnForm({ hsn_code: hsn.hsn_code, description: hsn.description || '', gst_rate: String(hsn.gst_rate || ''), applicable_from: hsn.applicable_from || '' });
    } else {
      setEditingHsn(null);
      setHsnForm({ hsn_code: '', description: '', gst_rate: '', applicable_from: getToday() });
    }
    setHsnError(''); setHsnDialog(true);
  };
  const handleHsnSubmit = async (e) => {
    e.preventDefault(); setHsnError(''); setHsnSubmitting(true);
    try {
      const rate = parseFloat(hsnForm.gst_rate) || 0;
      const payload = { hsn_code: hsnForm.hsn_code, description: hsnForm.description, gst_rate: rate, cgst_rate: rate / 2, sgst_rate: rate / 2, igst_rate: rate, applicable_from: hsnForm.applicable_from || '', effective_to: '' };
      if (editingHsn) { await updateHSN(editingHsn.id, payload); toast.success('HSN updated'); }
      else { await createHSN(payload); toast.success('HSN created'); }
      setHsnDialog(false); fetchHSN();
    } catch (err) { setHsnError(err.response?.data?.detail || 'Failed'); }
    setHsnSubmitting(false);
  };
  const handleDeleteHsn = async (id) => {
    if (!window.confirm('Delete this HSN rate entry?')) return;
    try { await deleteHSN(id); toast.success('HSN rate deleted'); fetchHSN(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete'); }
  };
  const handleDeleteHsnAll = async (hsnCode) => {
    if (!window.confirm(`Delete ALL rate entries for HSN ${hsnCode}?`)) return;
    try { await deleteHSNAll(hsnCode); toast.success(`HSN ${hsnCode} deleted`); setSelectedHsnCodes(prev => prev.filter(c => c !== hsnCode)); fetchHSN(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete'); }
  };
  const handleBulkDeleteHsn = async () => {
    if (!window.confirm(`Delete ${selectedHsnCodes.length} HSN codes?`)) return;
    let deleted = 0;
    for (const code of selectedHsnCodes) { try { await deleteHSNAll(code); deleted++; } catch (err) { console.error(`Failed to delete HSN ${code}:`, err); } }
    toast.success(`Deleted ${deleted} HSN codes`); setSelectedHsnCodes([]); fetchHSN();
  };
  const toggleHsnSelect = (code) => setSelectedHsnCodes(prev => prev.includes(code) ? prev.filter(c => c !== code) : [...prev, code]);
  const allHsnSelected = groupedHsn.length > 0 && groupedHsn.every(g => selectedHsnCodes.includes(g.hsn_code));
  const toggleAllHsn = () => { if (allHsnSelected) setSelectedHsnCodes([]); else setSelectedHsnCodes(groupedHsn.map(g => g.hsn_code)); };

  // Master Update GST Rate dialog
  const openRateDialog = () => {
    const initial = {};
    groupedHsn.forEach(g => { initial[g.hsn_code] = { gst_rate: '', applicable_from: getToday() }; });
    setRateUpdates(initial); setRateSubmitting(null); setRateDialog(true);
  };
  const updateRateField = (hsnCode, field, value) => setRateUpdates(prev => ({ ...prev, [hsnCode]: { ...prev[hsnCode], [field]: value } }));
  const handleSingleRateUpdate = async (hsnCode) => {
    const data = rateUpdates[hsnCode];
    if (!data?.gst_rate) { toast.error('Enter GST rate'); return; }
    setRateSubmitting(hsnCode);
    try {
      const openRates = hsnList.filter(h => h.hsn_code === hsnCode && !h.effective_to);
      for (const cr of openRates) {
        if (data.applicable_from) { const d = new Date(data.applicable_from); d.setDate(d.getDate() - 1); await updateHSN(cr.id, { effective_to: d.toISOString().split('T')[0] }); }
      }
      const existing = hsnList.find(h => h.hsn_code === hsnCode);
      const rate = parseFloat(data.gst_rate) || 0;
      await createHSN({ hsn_code: hsnCode, description: existing?.description || '', gst_rate: rate, cgst_rate: rate / 2, sgst_rate: rate / 2, igst_rate: rate, applicable_from: data.applicable_from || '', effective_to: '' });
      toast.success(`${hsnCode}: updated to ${rate}%`); updateRateField(hsnCode, 'gst_rate', ''); fetchHSN();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    setRateSubmitting(null);
  };

  // Per-row quick rate update
  const openQuickRate = (hsnCode) => { setQuickRateHsn(hsnCode); setQuickRateForm({ gst_rate: '', applicable_from: getToday() }); setQuickRateDialog(true); };
  const handleQuickRateSubmit = async (e) => {
    e.preventDefault(); setQuickRateSubmitting(true);
    try {
      const openRates = hsnList.filter(h => h.hsn_code === quickRateHsn && !h.effective_to);
      for (const cr of openRates) {
        if (quickRateForm.applicable_from) { const d = new Date(quickRateForm.applicable_from); d.setDate(d.getDate() - 1); await updateHSN(cr.id, { effective_to: d.toISOString().split('T')[0] }); }
      }
      const existing = hsnList.find(h => h.hsn_code === quickRateHsn);
      const rate = parseFloat(quickRateForm.gst_rate) || 0;
      await createHSN({ hsn_code: quickRateHsn, description: existing?.description || '', gst_rate: rate, cgst_rate: rate / 2, sgst_rate: rate / 2, igst_rate: rate, applicable_from: quickRateForm.applicable_from || '', effective_to: '' });
      toast.success(`${quickRateHsn}: updated to ${rate}%`); setQuickRateDialog(false); fetchHSN();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    setQuickRateSubmitting(false);
  };

  // ===== TAB CHANGE =====
  useEffect(() => {
    if (activeTab === 'products') fetchProducts();
    if (activeTab === 'customers') fetchCustomers();
    if (activeTab === 'supplier-groups') fetchSG();
    if (activeTab === 'supplier-firms') fetchSuppliers();
    if (activeTab === 'entry-ledger') fetchEL();
    if (activeTab === 'hsn') fetchHSN();
  }, [activeTab]); // eslint-disable-line

  const getHeaderActions = () => {
    switch (activeTab) {
      case 'products':
        return <Button size="sm" onClick={() => { resetProductForm(); setProductDialog(true); }} data-testid="add-product-btn"><Plus className="h-4 w-4 mr-1" />Add Product</Button>;
      case 'customers':
        return <Button size="sm" onClick={() => { resetCustomerForm(); setCustomerDialog(true); }} data-testid="add-customer-btn"><Plus className="h-4 w-4 mr-1" />Add Customer</Button>;
      case 'supplier-groups':
        return <Button size="sm" onClick={() => openSGDialog()} data-testid="add-sg-btn"><Plus className="h-4 w-4 mr-1" />Add Group</Button>;
      case 'supplier-firms':
        return <Button size="sm" onClick={() => openSupplierDialog()} data-testid="add-supplier-btn"><Plus className="h-4 w-4 mr-1" />Add Firm</Button>;
      case 'entry-ledger':
        return <Button size="sm" onClick={() => { setElForm({ name: '', category: '', type: 'income' }); setElDialog(true); }} data-testid="add-el-btn"><Plus className="h-4 w-4 mr-1" />Add Ledger</Button>;
      case 'hsn':
        return (
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={openRateDialog} data-testid="update-gst-rate-btn"><RefreshCw className="h-4 w-4 mr-1" />Update GST Rate</Button>
            <Button size="sm" onClick={() => openHsnDialog()} data-testid="add-hsn-btn"><Plus className="h-4 w-4 mr-1" />Add HSN</Button>
          </div>
        );
      default:
        return null;
    }
  };

  // Helper for simple table
  const SimpleTable = ({ headers, rows, emptyMsg }) => (
    <Card><CardContent className="p-0"><div className="overflow-x-auto">
      <Table>
        <TableHeader><TableRow>{headers.map((h, i) => <TableHead key={i} className={h.className}>{h.label}</TableHead>)}</TableRow></TableHeader>
        <TableBody>{rows.length === 0 ? <TableRow><TableCell colSpan={headers.length} className="text-center py-8 text-muted-foreground">{emptyMsg}</TableCell></TableRow> : rows}</TableBody>
      </Table>
    </div></CardContent></Card>
  );

  return (
    <>
    <SectionTiles
      pageTitle="Master Data"
      pageDescription="Manage core data"
      sections={SECTION_DEFS}
      activeSection={activeTab}
      onSelect={setActiveTab}
      headerActions={getHeaderActions()}
    >
      {/* PRODUCTS */}
      {activeTab === 'products' && (productsLoading ? <Skeleton className="h-64 w-full" /> :
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <Button variant={!filterCategory ? 'default' : 'outline'} size="sm" className="text-xs h-7" onClick={() => handleCategoryFilter('')} data-testid="filter-cat-all"><kbd className="mr-1 text-[9px] opacity-60 hidden sm:inline">1</kbd>All</Button>
            {CATEGORY_OPTIONS.map((cat, i) => (
              <Button key={cat.value} variant={filterCategory === cat.value ? 'default' : 'outline'} size="sm" className="text-xs h-7" onClick={() => handleCategoryFilter(cat.value)} data-testid={`filter-cat-${cat.value}`}><kbd className="mr-1 text-[9px] opacity-60 hidden sm:inline">{i + 2}</kbd>{cat.label}</Button>
            ))}
            {productsMeta.groups.length > 0 && (
              <>
                <span className="text-muted-foreground text-xs ml-2">Group:</span>
                <Button variant={!filterGroup ? 'default' : 'outline'} size="sm" className="text-xs h-7" onClick={() => handleGroupFilter('')}><kbd className="mr-1 text-[9px] opacity-60 hidden sm:inline">G0</kbd>All</Button>
                {productsMeta.groups.map((g, i) => (
                  <Button key={g} variant={filterGroup === g ? 'default' : 'outline'} size="sm" className="text-xs h-7" onClick={() => handleGroupFilter(g)} data-testid={`filter-grp-${g}`}><kbd className="mr-1 text-[9px] opacity-60 hidden sm:inline">G{i + 1}</kbd>{g}</Button>
                ))}
              </>
            )}
          </div>

          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10 cursor-pointer" onClick={toggleAll}><Checkbox checked={allSelected} className="pointer-events-none" data-testid="select-all-products" /></TableHead>
                      <TableHead><SortableHeader label="Name" sortKey="name" currentSortKey={prodSortKey} sortDirection={prodSortDir} onSort={handleProdSort} /></TableHead>
                      <TableHead><SortableHeader label="Category" sortKey="category" currentSortKey={prodSortKey} sortDirection={prodSortDir} onSort={handleProdSort} /></TableHead>
                      <TableHead className="hidden sm:table-cell"><SortableHeader label="Group" sortKey="group" currentSortKey={prodSortKey} sortDirection={prodSortDir} onSort={handleProdSort} /></TableHead>
                      <TableHead className="text-right hidden sm:table-cell"><SortableHeader label="KG/Bag" sortKey="kg_per_bag" currentSortKey={prodSortKey} sortDirection={prodSortDir} onSort={handleProdSort} className="justify-end" /></TableHead>
                      <TableHead className="hidden sm:table-cell">Brands</TableHead>
                      <TableHead className="hidden sm:table-cell">HSN</TableHead>
                      <TableHead className="w-[60px] sm:w-[80px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedProducts.length === 0 ? (
                      <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">No products found</TableCell></TableRow>
                    ) : sortedProducts.map(p => (
                      <React.Fragment key={p.id}>
                        <TableRow
                          className={`cursor-pointer sm:cursor-default ${selectedIds.includes(p.id) ? 'bg-muted/50' : ''} ${expandedMobileId === p.id ? 'bg-muted/30' : ''}`}
                          onClick={() => setExpandedMobileId(expandedMobileId === p.id ? null : p.id)}
                          data-testid={`product-row-${p.id}`}
                        >
                          <TableCell className="cursor-pointer" onClick={(e) => { e.stopPropagation(); toggleSelect(p.id); }}><Checkbox checked={selectedIds.includes(p.id)} className="pointer-events-none" /></TableCell>
                          <TableCell className="font-medium">{p.name}</TableCell>
                          <TableCell><Badge variant="outline" className="text-xs">{CATEGORY_LABELS[p.category] || p.category}</Badge></TableCell>
                          <TableCell className="hidden sm:table-cell text-sm">{p.group}</TableCell>
                          <TableCell className="text-right font-mono hidden sm:table-cell">{p.kg_per_bag}</TableCell>
                          <TableCell className="hidden sm:table-cell text-sm">{p.brands?.length ? p.brands.join(', ') : '-'}</TableCell>
                          <TableCell className="hidden sm:table-cell text-sm font-mono">{p.hsn_code || '-'}</TableCell>
                          <TableCell>
                            <div className="flex justify-end gap-0.5" onClick={(e) => e.stopPropagation()}>
                              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openProductDialog(p)} data-testid={`edit-product-${p.id}`}><Pencil className="h-3 w-3" /></Button>
                              <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive" onClick={() => handleDeleteProduct(p)} data-testid={`delete-product-${p.id}`}><Trash2 className="h-3 w-3" /></Button>
                            </div>
                          </TableCell>
                        </TableRow>
                        {expandedMobileId === p.id && (
                          <TableRow className="sm:hidden bg-muted/20">
                            <TableCell colSpan={8}>
                              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm py-1">
                                <span className="text-muted-foreground">Group</span><span className="text-right">{p.group}</span>
                                <span className="text-muted-foreground">KG/Bag</span><span className="text-right">{p.kg_per_bag}</span>
                                <span className="text-muted-foreground">Brands</span><span className="text-right">{p.brands?.length ? p.brands.join(', ') : '-'}</span>
                                <span className="text-muted-foreground">HSN Code</span><span className="text-right font-mono">{p.hsn_code || '-'}</span>
                                {p.default_gst_rate > 0 && <><span className="text-muted-foreground">GST Rate</span><span className="text-right">{p.default_gst_rate}%</span></>}
                                {p.description && <><span className="text-muted-foreground">Description</span><span className="text-right">{p.description}</span></>}
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* CUSTOMERS */}
      {activeTab === 'customers' && (customersLoading ? <Skeleton className="h-64 w-full" /> :
        <div className="space-y-4">
          {selectedCustomerIds.length > 0 && (
            <div className="fixed bottom-14 sm:bottom-14 left-1/2 -translate-x-1/2 z-50 flex items-center gap-4 px-5 py-3 rounded-full shadow-lg border bg-background" data-testid="customer-bulk-action-bar">
              <span className="text-sm font-medium">{selectedCustomerIds.length} selected</span>
              <Button variant="outline" size="sm" onClick={() => setSelectedCustomerIds([])}>Clear</Button>
              <Button variant="destructive" size="sm" onClick={handleBulkDeleteCustomers}><Trash2 className="h-4 w-4 mr-1" />Delete</Button>
            </div>
          )}
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10 cursor-pointer" onClick={toggleAllCustomers}><Checkbox checked={allCustomersSelected} className="pointer-events-none" data-testid="select-all-customers" /></TableHead>
                      <TableHead><SortableHeader label="Company" sortKey="company_name" currentSortKey={custSortKey} sortDirection={custSortDir} onSort={handleCustSort} /></TableHead>
                      <TableHead className="hidden sm:table-cell">Company Email</TableHead>
                      <TableHead className="hidden sm:table-cell">Contacts</TableHead>
                      <TableHead className="text-center"><SortableHeader label="Credit Days" sortKey="credit_days" currentSortKey={custSortKey} sortDirection={custSortDir} onSort={handleCustSort} /></TableHead>
                      <TableHead className="w-[60px] sm:w-[80px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {customers.length === 0 ? (
                      <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No customers found</TableCell></TableRow>
                    ) : sortedCustomers.map(customer => (
                      <TableRow key={customer.id} data-testid={`customer-row-${customer.id}`} className={selectedCustomerIds.includes(customer.id) ? 'bg-muted/50' : ''}>
                        <TableCell className="cursor-pointer" onClick={() => toggleCustomerSelect(customer.id)}><Checkbox checked={selectedCustomerIds.includes(customer.id)} className="pointer-events-none" /></TableCell>
                        <TableCell className="font-medium">{customer.company_name || customer.name}</TableCell>
                        <TableCell className="hidden sm:table-cell">{customer.company_email || '-'}</TableCell>
                        <TableCell className="hidden sm:table-cell">
                          {customer.contact_persons?.length > 0 ? (
                            <div className="space-y-1">
                              {customer.contact_persons.slice(0, 2).map((cp, i) => (
                                <div key={i} className="text-xs">
                                  <span className="font-medium">{cp.name || 'Unnamed'}</span>
                                  {cp.department && <span className="text-muted-foreground"> ({cp.department})</span>}
                                  {cp.mobile_number && <span className="text-muted-foreground ml-1">- {cp.mobile_number}</span>}
                                </div>
                              ))}
                              {customer.contact_persons.length > 2 && <span className="text-xs text-muted-foreground">+{customer.contact_persons.length - 2} more</span>}
                            </div>
                          ) : '-'}
                        </TableCell>
                        <TableCell className="text-center">{customer.credit_days} days</TableCell>
                        <TableCell>
                          <div className="flex justify-end gap-0.5" onClick={(e) => e.stopPropagation()}>
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openCustomerDialog(customer)} data-testid={`edit-customer-${customer.id}`}><Pencil className="h-3 w-3" /></Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive" onClick={() => handleDeleteCustomer(customer)} data-testid={`delete-customer-${customer.id}`}><Trash2 className="h-3 w-3" /></Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* SUPPLIER GROUPS */}
      {activeTab === 'supplier-groups' && (sgLoading ? <Skeleton className="h-64 w-full" /> :
        <SimpleTable headers={[{ label: 'Name' }, { label: 'Credit Days' }, { label: '', className: 'w-20' }]} emptyMsg="No groups" rows={supplierGroups.map(g => (
          <TableRow key={g.id}><TableCell className="font-medium">{g.name}</TableCell><TableCell>{g.credit_days || 30} days</TableCell><TableCell><div className="flex gap-0.5 justify-end"><Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openSGDialog(g)}><Pencil className="h-3 w-3" /></Button><Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => handleDeleteSG(g)}><Trash2 className="h-3 w-3" /></Button></div></TableCell></TableRow>
        ))} />
      )}

      {/* SUPPLIER FIRMS */}
      {activeTab === 'supplier-firms' && (suppliersLoading ? <Skeleton className="h-64 w-full" /> :
        <SimpleTable headers={[{ label: 'Firm Name' }, { label: 'Group', className: 'hidden sm:table-cell' }, { label: 'Credit', className: 'hidden sm:table-cell' }, { label: 'GSTIN', className: 'hidden sm:table-cell' }, { label: '', className: 'w-20' }]} emptyMsg="No firms" rows={suppliers.map(s => (
          <TableRow key={s.id}><TableCell className="font-medium">{s.company_name}</TableCell><TableCell className="hidden sm:table-cell">{s.group_name || '-'}</TableCell><TableCell className="hidden sm:table-cell">{s.credit_days || 30}d</TableCell><TableCell className="hidden sm:table-cell font-mono">{s.gstin || '-'}</TableCell><TableCell><div className="flex gap-0.5 justify-end"><Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openSupplierDialog(s)}><Pencil className="h-3 w-3" /></Button><Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => handleDeleteSupplier(s)}><Trash2 className="h-3 w-3" /></Button></div></TableCell></TableRow>
        ))} />
      )}

      {/* ENTRY LEDGER */}
      {activeTab === 'entry-ledger' && (elLoading ? <Skeleton className="h-64 w-full" /> :
        <SimpleTable headers={[{ label: 'Name' }, { label: 'Category' }, { label: 'Type' }, { label: '', className: 'w-10' }]} emptyMsg="No ledgers" rows={entryLedgers.map(l => (
          <TableRow key={l.id}><TableCell className="font-medium">{l.name}</TableCell><TableCell>{l.category || '-'}</TableCell><TableCell><Badge variant={l.type === 'income' ? 'default' : 'secondary'}>{l.type}</Badge></TableCell><TableCell><Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => { if (window.confirm(`Delete ${l.name}?`)) deleteEntryLedger(l.id).then(() => { toast.success('Deleted'); fetchEL(); }).catch(e => toast.error(formatApiErrorDetail(e.response?.data?.detail))); }}><Trash2 className="h-3 w-3" /></Button></TableCell></TableRow>
        ))} />
      )}

      {/* HSN */}
      {activeTab === 'hsn' && (hsnLoading ? <Skeleton className="h-64 w-full" /> :
        <div className="space-y-4">
          <div className="rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10 cursor-pointer" onClick={toggleAllHsn}><Checkbox checked={allHsnSelected} className="pointer-events-none" data-testid="select-all-hsn" /></TableHead>
                  <TableHead className="w-[30px]"></TableHead>
                  <TableHead>HSN Code</TableHead>
                  <TableHead className="hidden sm:table-cell">Description</TableHead>
                  <TableHead className="text-right">Current GST %</TableHead>
                  <TableHead className="hidden sm:table-cell">From</TableHead>
                  <TableHead className="w-[120px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {groupedHsn.length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">No HSN codes added yet</TableCell></TableRow>
                ) : groupedHsn.map(g => {
                  const isExpanded = expandedHsn === g.hsn_code;
                  return (
                    <React.Fragment key={g.hsn_code}>
                      <TableRow
                        className={`cursor-pointer hover:bg-muted/50 ${isExpanded ? 'bg-muted/30' : ''} ${selectedHsnCodes.includes(g.hsn_code) ? 'bg-muted/50' : ''}`}
                        onClick={() => setExpandedHsn(isExpanded ? null : g.hsn_code)}
                        data-testid={`hsn-row-${g.hsn_code}`}
                      >
                        <TableCell className="cursor-pointer" onClick={(e) => { e.stopPropagation(); toggleHsnSelect(g.hsn_code); }}><Checkbox checked={selectedHsnCodes.includes(g.hsn_code)} className="pointer-events-none" /></TableCell>
                        <TableCell className="w-[30px] px-2">
                          {g.history.length > 1 ? (isExpanded ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" /> : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />) : <span className="w-3.5" />}
                        </TableCell>
                        <TableCell className="font-mono font-medium">{g.hsn_code}</TableCell>
                        <TableCell className="hidden sm:table-cell text-muted-foreground">{g.description || '-'}</TableCell>
                        <TableCell className="text-right font-medium">{g.latest.gst_rate}%</TableCell>
                        <TableCell className="hidden sm:table-cell text-muted-foreground">{g.latest.applicable_from ? formatDate(g.latest.applicable_from) : '-'}</TableCell>
                        <TableCell>
                          <div className="flex gap-0.5 justify-end" onClick={(e) => e.stopPropagation()}>
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-primary" onClick={() => openQuickRate(g.hsn_code)} title="Update GST Rate" data-testid={`update-rate-${g.hsn_code}`}><RefreshCw className="h-3.5 w-3.5" /></Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openHsnDialog(g.latest)} title="Edit" data-testid={`edit-hsn-${g.hsn_code}`}><Pencil className="h-3.5 w-3.5" /></Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => handleDeleteHsnAll(g.hsn_code)} title="Delete HSN" data-testid={`delete-hsn-${g.hsn_code}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                          </div>
                        </TableCell>
                      </TableRow>
                      {isExpanded && g.history.length > 0 && (
                        <TableRow className="bg-muted/10">
                          <TableCell colSpan={7} className="p-0">
                            <div className="px-6 py-3">
                              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Rate History — {g.hsn_code}</p>
                              <Table>
                                <TableHeader>
                                  <TableRow>
                                    <TableHead className="text-xs py-1.5">GST %</TableHead>
                                    <TableHead className="text-xs py-1.5">From</TableHead>
                                    <TableHead className="text-xs py-1.5">To</TableHead>
                                    <TableHead className="text-xs py-1.5 w-[60px]"></TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {g.history.map((h) => {
                                    const isCurrent = !h.effective_to;
                                    return (
                                    <TableRow key={h.id} className={isCurrent ? 'font-medium' : 'text-muted-foreground'}>
                                      <TableCell className="text-xs py-1.5 font-mono">{h.gst_rate}%{isCurrent ? ' (current)' : ''}</TableCell>
                                      <TableCell className="text-xs py-1.5">{h.applicable_from ? formatDate(h.applicable_from) : '-'}</TableCell>
                                      <TableCell className="text-xs py-1.5">{h.effective_to ? formatDate(h.effective_to) : '-'}</TableCell>
                                      <TableCell className="text-xs py-1.5">
                                        <div className="flex gap-0.5">
                                          <Button variant="ghost" size="icon" className="h-5 w-5" onClick={() => openHsnDialog(h)} title="Edit"><Pencil className="h-2.5 w-2.5" /></Button>
                                          <Button variant="ghost" size="icon" className="h-5 w-5 text-destructive" onClick={() => handleDeleteHsn(h.id)} title="Delete"><Trash2 className="h-2.5 w-2.5" /></Button>
                                        </div>
                                      </TableCell>
                                    </TableRow>
                                    );
                                  })}
                                </TableBody>
                              </Table>
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {/* Bulk action bar */}
      {selectedIds.length > 0 && (
        <div className="fixed bottom-14 sm:bottom-14 left-1/2 -translate-x-1/2 z-50 flex items-center gap-4 px-5 py-3 rounded-full shadow-lg border bg-background" data-testid="bulk-action-bar">
          <span className="text-sm font-medium">{selectedIds.length} selected</span>
          <Button variant="outline" size="sm" onClick={() => setSelectedIds([])}>Clear</Button>
          <Button variant="destructive" size="sm" onClick={handleBulkDeleteProducts}><Trash2 className="h-4 w-4 mr-1" />Delete</Button>
        </div>
      )}

      {/* PRODUCT DIALOG */}
      <Dialog open={productDialog} onOpenChange={setProductDialog}>
        <DialogContent><DialogHeader><DialogTitle>{editingProduct ? 'Edit Product' : 'New Product'}</DialogTitle></DialogHeader>
          <EnterForm onSubmit={handleProductSubmit} className="space-y-4">
            {productError && <Alert variant="destructive"><AlertCircle className="h-4 w-4" /><AlertDescription>{productError}</AlertDescription></Alert>}
            <div className="space-y-2"><Label>Name</Label><Input value={productForm.name} onChange={e => setProductForm(p => ({...p, name: e.target.value}))} placeholder="e.g. T Gyp" required data-testid="product-name-input" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Category</Label>
                <SearchableSelect options={CATEGORY_OPTIONS} value={productForm.category} onChange={v => setProductForm(p => ({...p, category: v}))} placeholder="Select category" data-testid="product-category-select" />
              </div>
              <div className="space-y-2"><Label>Group</Label>
                <SearchableSelect options={groupOptions} value={productForm.group} onChange={v => setProductForm(p => ({...p, group: v}))} onCreateNew={v => setProductForm(p => ({...p, group: v}))} placeholder="Select or type group" createNewLabel="Add group" data-testid="product-group-select" />
              </div>
            </div>
            <div className="space-y-2"><Label>KG per Bag</Label><Input type="number" step="0.01" min="0" value={productForm.kg_per_bag} onChange={e => setProductForm(p => ({...p, kg_per_bag: e.target.value}))} placeholder="e.g. 50" data-testid="product-kgperbag" /></div>
            <div className="space-y-2"><Label>Brands (comma separated)</Label><Input value={productForm.brands} onChange={e => setProductForm(p => ({...p, brands: e.target.value}))} placeholder="e.g. Ambuja, Sanstar, Sayaji" data-testid="product-brands-input" /></div>
            <div className="space-y-2"><Label>Description <span className="text-xs text-muted-foreground">(optional)</span></Label><Input value={productForm.description} onChange={e => setProductForm(p => ({...p, description: e.target.value}))} placeholder="Product description" data-testid="product-description-input" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>HSN Code</Label>
                <SearchableSelect options={hsnOptions} value={productForm.hsn_code} onChange={v => { const hsn = hsnCodes.filter(h => h.hsn_code === v).sort((a, b) => (b.applicable_from || '').localeCompare(a.applicable_from || ''))[0]; setProductForm(p => ({...p, hsn_code: v, default_gst_rate: hsn ? String(hsn.gst_rate) : p.default_gst_rate})); }} onCreateNew={typed => { setAddHsnForm({ hsn_code: typed, description: '', gst_rate: '', applicable_from: getToday() }); setAddHsnOpen(true); }} createNewLabel="Add HSN" placeholder="Type or select HSN" data-testid="product-hsn-select" />
              </div>
              <div className="space-y-2"><Label>Default GST Rate %</Label><Input type="number" step="0.01" value={productForm.default_gst_rate} onChange={e => setProductForm(p => ({...p, default_gst_rate: e.target.value}))} placeholder="e.g. 18" data-testid="product-gst-rate" /></div>
            </div>
            <DialogFooter>
              <FormShortcutHints />
              <Button type="button" variant="outline" onClick={() => setProductDialog(false)}>Cancel</Button>
              <Button type="submit" disabled={productSubmitting || !productForm.name || !productForm.category || !productForm.group} data-testid="product-submit-btn">{productSubmitting ? 'Saving...' : editingProduct ? 'Update' : 'Create'}</Button>
            </DialogFooter>
          </EnterForm>
        </DialogContent>
      </Dialog>

      {/* ADD HSN INLINE */}
      <Dialog open={addHsnOpen} onOpenChange={setAddHsnOpen}>
        <DialogContent className="max-w-sm"><DialogHeader><DialogTitle>Add HSN Code</DialogTitle></DialogHeader>
          <EnterForm onSubmit={handleAddHsnSubmit} className="space-y-4">
            <div className="space-y-2"><Label>HSN Code *</Label><Input value={addHsnForm.hsn_code} onChange={e => setAddHsnForm(p => ({...p, hsn_code: e.target.value}))} className="font-mono" required data-testid="inline-hsn-code" /></div>
            <div className="space-y-2"><Label>Description</Label><Input value={addHsnForm.description} onChange={e => setAddHsnForm(p => ({...p, description: e.target.value}))} data-testid="inline-hsn-desc" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>GST Rate % *</Label><Input type="number" step="0.01" value={addHsnForm.gst_rate} onChange={e => setAddHsnForm(p => ({...p, gst_rate: e.target.value}))} required data-testid="inline-hsn-rate" /></div>
              <div className="space-y-2"><Label>From</Label><DateInput value={addHsnForm.applicable_from} onChange={e => setAddHsnForm(p => ({...p, applicable_from: e.target.value}))} data-testid="inline-hsn-from" /></div>
            </div>
            <DialogFooter><FormShortcutHints /><Button type="button" variant="outline" onClick={() => setAddHsnOpen(false)}>Cancel</Button><Button type="submit" disabled={addHsnSubmitting} data-testid="inline-hsn-submit">{addHsnSubmitting ? 'Creating...' : 'Create HSN'}</Button></DialogFooter>
          </EnterForm>
        </DialogContent>
      </Dialog>

      {/* CUSTOMER DIALOG */}
      <Dialog open={customerDialog} onOpenChange={(open) => { setCustomerDialog(open); if (!open) resetCustomerForm(); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" onKeyDownCapture={(e) => { if (e.altKey && e.code === 'KeyC') { e.preventDefault(); e.stopPropagation(); const el = document.activeElement; if (el && el.tagName === 'INPUT') el.blur(); addContactPerson(); if (el) setTimeout(() => el.focus(), 0); } }}>
          <DialogHeader><DialogTitle>{editingCustomer ? 'Edit Customer' : 'Add Customer'}</DialogTitle></DialogHeader>
          <EnterForm onSubmit={handleCustomerSubmit} className="space-y-4">
            {customerError && <Alert variant="destructive"><AlertCircle className="h-4 w-4" /><AlertDescription>{customerError}</AlertDescription></Alert>}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Company Name</Label><Input placeholder="Company name" value={customerForm.company_name} onChange={e => setCustomerForm(p => ({...p, company_name: e.target.value}))} required data-testid="customer-company-name-input" /></div>
              <div className="space-y-2"><Label>Company Email</Label><Input type="email" placeholder="info@company.com" value={customerForm.company_email} onChange={e => setCustomerForm(p => ({...p, company_email: e.target.value}))} data-testid="customer-company-email-input" /></div>
            </div>
            <div className="space-y-2"><Label>Credit Days</Label><Input type="number" min="0" placeholder="30" value={customerForm.credit_days} onChange={e => setCustomerForm(p => ({...p, credit_days: e.target.value}))} required data-testid="customer-credit-days-input" /></div>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-base font-semibold">Contact Persons</Label>
                <Button type="button" variant="outline" size="sm" onClick={addContactPerson} data-testid="add-contact-person-btn"><Plus className="h-3 w-3 mr-1" />Add Contact <kbd className="ml-1.5 px-1 py-0.5 text-[10px] font-mono bg-muted border rounded">Alt+C</kbd></Button>
              </div>
              {customerForm.contact_persons.map((cp, idx) => (
                <Card key={idx} className="p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-muted-foreground">Contact {idx + 1}</span>
                    {customerForm.contact_persons.length > 1 && <Button type="button" variant="ghost" size="icon" className="h-6 w-6" onClick={() => removeContactPerson(idx)} data-testid={`remove-contact-${idx}`}><X className="h-3 w-3" /></Button>}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <Input placeholder="Name" value={cp.name} onChange={e => updateContactPerson(idx, 'name', e.target.value)} data-testid={`contact-name-${idx}`} />
                    <Input placeholder="Department" value={cp.department} onChange={e => updateContactPerson(idx, 'department', e.target.value)} data-testid={`contact-dept-${idx}`} />
                    <PhoneInput value={cp.mobile_number} onChange={e => updateContactPerson(idx, 'mobile_number', e.target.value)} data-testid={`contact-mobile-${idx}`} />
                    <Input type="email" placeholder="Email" value={cp.email} onChange={e => updateContactPerson(idx, 'email', e.target.value)} data-testid={`contact-email-${idx}`} />
                  </div>
                </Card>
              ))}
            </div>
            <DialogFooter>
              <FormShortcutHints />
              <Button type="button" variant="outline" onClick={() => setCustomerDialog(false)}>Cancel</Button>
              <Button type="submit" disabled={customerSubmitting} data-testid="customer-submit-button">{customerSubmitting ? 'Saving...' : editingCustomer ? 'Update' : 'Add'}</Button>
            </DialogFooter>
          </EnterForm>
        </DialogContent>
      </Dialog>

      {/* SUPPLIER GROUP DIALOG */}
      <Dialog open={sgDialog} onOpenChange={setSgDialog}>
        <DialogContent className="max-w-sm"><DialogHeader><DialogTitle>{editingSG ? 'Edit Supplier Group' : 'New Supplier Group'}</DialogTitle></DialogHeader>
          <EnterForm onSubmit={handleSGSubmit} className="space-y-4">
            {sgError && <Alert variant="destructive"><AlertCircle className="h-4 w-4" /><AlertDescription>{sgError}</AlertDescription></Alert>}
            <div className="space-y-2"><Label>Name *</Label><Input value={sgForm.name} onChange={e => setSgForm(p => ({...p, name: e.target.value}))} required /></div>
            <div className="space-y-2"><Label>Credit Days</Label><Input type="number" value={sgForm.credit_days} onChange={e => setSgForm(p => ({...p, credit_days: e.target.value}))} placeholder="30" /></div>
            <DialogFooter><FormShortcutHints /><Button type="button" variant="outline" onClick={() => setSgDialog(false)}>Cancel</Button><Button type="submit" disabled={sgSubmitting}>{sgSubmitting ? 'Saving...' : editingSG ? 'Update' : 'Create'}</Button></DialogFooter>
          </EnterForm>
        </DialogContent>
      </Dialog>

      {/* SUPPLIER FIRM DIALOG */}
      <Dialog open={supplierDialog} onOpenChange={setSupplierDialog}>
        <DialogContent><DialogHeader><DialogTitle>{editingSupplier ? 'Edit Supplier Firm' : 'New Supplier Firm'}</DialogTitle></DialogHeader>
          <EnterForm onSubmit={handleSupplierSubmit} className="space-y-4">
            {supplierError && <Alert variant="destructive"><AlertCircle className="h-4 w-4" /><AlertDescription>{supplierError}</AlertDescription></Alert>}
            <div className="space-y-2"><Label>Firm Name *</Label><Input value={supplierForm.company_name} onChange={e => setSupplierForm(p => ({...p, company_name: e.target.value}))} required /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Supplier Group</Label><SearchableSelect options={supplierGroups.map(g => ({ value: g.id, label: g.name }))} value={supplierForm.group_id} onChange={v => { const g = supplierGroups.find(x => x.id === v); setSupplierForm(p => ({...p, group_id: v, credit_days: g ? String(g.credit_days || 30) : p.credit_days})); }} placeholder="Select group" /></div>
              <div className="space-y-2"><Label>Credit Days</Label><Input type="number" value={supplierForm.credit_days} onChange={e => setSupplierForm(p => ({...p, credit_days: e.target.value}))} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3"><div className="space-y-2"><Label>GSTIN</Label><Input value={supplierForm.gstin} onChange={e => setSupplierForm(p => ({...p, gstin: e.target.value}))} /></div><div className="space-y-2"><Label>Contact Person</Label><Input value={supplierForm.contact_person} onChange={e => setSupplierForm(p => ({...p, contact_person: e.target.value}))} /></div></div>
            <div className="grid grid-cols-2 gap-3"><div className="space-y-2"><Label>Phone</Label><Input value={supplierForm.phone} onChange={e => setSupplierForm(p => ({...p, phone: e.target.value}))} /></div><div className="space-y-2"><Label>Address</Label><Input value={supplierForm.address} onChange={e => setSupplierForm(p => ({...p, address: e.target.value}))} /></div></div>
            <DialogFooter><FormShortcutHints /><Button type="button" variant="outline" onClick={() => setSupplierDialog(false)}>Cancel</Button><Button type="submit" disabled={supplierSubmitting}>{supplierSubmitting ? 'Saving...' : editingSupplier ? 'Update' : 'Create'}</Button></DialogFooter>
          </EnterForm>
        </DialogContent>
      </Dialog>

      {/* ENTRY LEDGER DIALOG */}
      <Dialog open={elDialog} onOpenChange={setElDialog}>
        <DialogContent className="max-w-sm"><DialogHeader><DialogTitle>New Entry Ledger</DialogTitle></DialogHeader>
          <EnterForm onSubmit={handleELSubmit} className="space-y-4">
            <div className="space-y-2"><Label>Name *</Label><Input value={elForm.name} onChange={e => setElForm(p => ({...p, name: e.target.value}))} required /></div>
            <div className="space-y-2"><Label>Category</Label><Input value={elForm.category} onChange={e => setElForm(p => ({...p, category: e.target.value}))} placeholder="e.g. Salary, Rent" /></div>
            <div className="space-y-2"><Label>Type</Label><SearchableSelect options={[{ value: 'income', label: 'Income' }, { value: 'expense', label: 'Expense' }]} value={elForm.type} onChange={v => setElForm(p => ({...p, type: v}))} /></div>
            <DialogFooter><FormShortcutHints /><Button type="button" variant="outline" onClick={() => setElDialog(false)}>Cancel</Button><Button type="submit" disabled={elSubmitting}>{elSubmitting ? 'Creating...' : 'Create'}</Button></DialogFooter>
          </EnterForm>
        </DialogContent>
      </Dialog>

      {/* HSN Add/Edit Dialog */}
      <Dialog open={hsnDialog} onOpenChange={setHsnDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editingHsn ? 'Edit HSN Code' : 'Add HSN Code'}</DialogTitle></DialogHeader>
          <EnterForm onSubmit={handleHsnSubmit} className="space-y-4">
            {hsnError && <div className="text-sm text-destructive bg-destructive/10 p-2 rounded">{hsnError}</div>}
            <div className="space-y-2"><Label>HSN Code *</Label><Input value={hsnForm.hsn_code} onChange={e => setHsnForm(p => ({...p, hsn_code: e.target.value}))} required className="font-mono" placeholder="e.g. 2520" readOnly={!!editingHsn} data-testid="hsn-code-input" /></div>
            <div className="space-y-2"><Label>Description</Label><Input value={hsnForm.description} onChange={e => setHsnForm(p => ({...p, description: e.target.value}))} placeholder="Description of goods" data-testid="hsn-description-input" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>GST Rate %</Label><Input type="number" step="0.01" value={hsnForm.gst_rate} onChange={e => setHsnForm(p => ({...p, gst_rate: e.target.value}))} placeholder="e.g. 18" data-testid="hsn-gst-rate-input" /></div>
              <div className="space-y-2"><Label>Applicable From</Label><DateInput value={hsnForm.applicable_from} onChange={e => setHsnForm(p => ({...p, applicable_from: e.target.value}))} data-testid="hsn-applicable-from" /></div>
            </div>
            <DialogFooter>
              <FormShortcutHints />
              <Button type="button" variant="outline" onClick={() => setHsnDialog(false)}>Cancel</Button>
              <Button type="submit" disabled={hsnSubmitting} data-testid="hsn-submit-btn">{hsnSubmitting ? 'Saving...' : editingHsn ? 'Update' : 'Create'}</Button>
            </DialogFooter>
          </EnterForm>
        </DialogContent>
      </Dialog>

      {/* Master Update GST Rate Dialog */}
      <Dialog open={rateDialog} onOpenChange={setRateDialog}>
        <DialogContent className="max-w-xl max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Update GST Rates</DialogTitle></DialogHeader>
          <div className="space-y-1">
            {groupedHsn.length === 0 ? (
              <p className="text-center py-4 text-muted-foreground">No HSN codes found. Add HSN codes first.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">HSN Code</TableHead>
                    <TableHead className="text-xs hidden sm:table-cell">Description</TableHead>
                    <TableHead className="text-xs text-right">Current</TableHead>
                    <TableHead className="text-xs">New Rate %</TableHead>
                    <TableHead className="text-xs">From</TableHead>
                    <TableHead className="text-xs w-[80px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {groupedHsn.map(g => (
                    <TableRow key={g.hsn_code}>
                      <TableCell className="font-mono font-medium text-sm py-2">{g.hsn_code}</TableCell>
                      <TableCell className="text-xs text-muted-foreground hidden sm:table-cell py-2">{g.description || '-'}</TableCell>
                      <TableCell className="text-right text-sm font-mono py-2">{g.latest.gst_rate}%</TableCell>
                      <TableCell className="py-2">
                        <Input type="number" step="0.01" className="h-8 w-20 text-sm" value={rateUpdates[g.hsn_code]?.gst_rate || ''} onChange={e => updateRateField(g.hsn_code, 'gst_rate', e.target.value)} placeholder="%" data-testid={`rate-input-${g.hsn_code}`} />
                      </TableCell>
                      <TableCell className="py-2">
                        <DateInput className="h-8 w-32 text-sm" value={rateUpdates[g.hsn_code]?.applicable_from || ''} onChange={e => updateRateField(g.hsn_code, 'applicable_from', e.target.value)} data-testid={`rate-date-${g.hsn_code}`} />
                      </TableCell>
                      <TableCell className="py-2">
                        <Button size="sm" className="h-7 text-xs" disabled={rateSubmitting === g.hsn_code || !rateUpdates[g.hsn_code]?.gst_rate} onClick={() => handleSingleRateUpdate(g.hsn_code)} data-testid={`rate-update-${g.hsn_code}`}>
                          {rateSubmitting === g.hsn_code ? '...' : 'Update'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setRateDialog(false)}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Per-row Quick Rate Update Dialog */}
      <Dialog open={quickRateDialog} onOpenChange={setQuickRateDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Update GST Rate — {quickRateHsn}</DialogTitle></DialogHeader>
          <form onSubmit={handleQuickRateSubmit} className="space-y-4">
            {(() => {
              const current = hsnList.filter(h => h.hsn_code === quickRateHsn).sort((a, b) => (b.applicable_from || '').localeCompare(a.applicable_from || ''))[0];
              return current ? (
                <div className="text-xs text-muted-foreground bg-muted/50 rounded px-3 py-2">
                  Current rate: <span className="font-mono font-medium">{current.gst_rate}%</span>
                  {current.applicable_from && <> from {formatDate(current.applicable_from)}</>}
                </div>
              ) : null;
            })()}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>New GST Rate %</Label><Input autoFocus type="number" step="0.01" value={quickRateForm.gst_rate} onChange={e => setQuickRateForm(p => ({...p, gst_rate: e.target.value}))} placeholder="e.g. 18" required data-testid="quick-rate-input" /></div>
              <div className="space-y-2"><Label>Applicable From</Label><DateInput value={quickRateForm.applicable_from} onChange={e => setQuickRateForm(p => ({...p, applicable_from: e.target.value}))} required data-testid="quick-rate-date" /></div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setQuickRateDialog(false)}>Cancel</Button>
              <Button type="submit" disabled={quickRateSubmitting || !quickRateForm.gst_rate} data-testid="quick-rate-submit">{quickRateSubmitting ? 'Updating...' : 'Update Rate'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

    </SectionTiles>

    {/* HSN Bulk action bar */}
    {selectedHsnCodes.length > 0 && (
      <div className="fixed bottom-14 sm:bottom-14 left-1/2 -translate-x-1/2 z-50 flex items-center gap-4 px-5 py-3 rounded-full shadow-lg border bg-background" data-testid="hsn-bulk-action-bar">
        <span className="text-sm font-medium">{selectedHsnCodes.length} selected</span>
        <Button variant="outline" size="sm" onClick={() => setSelectedHsnCodes([])}>Clear</Button>
        <Button variant="destructive" size="sm" onClick={handleBulkDeleteHsn}><Trash2 className="h-4 w-4 mr-1" />Delete selected</Button>
      </div>
    )}
    </>
  );
}
